/* eslint-disable react/jsx-no-target-blank */
import React, { useState } from 'react'

import { Button, CardHeader } from '@mui/material'
import { addDays, differenceInDays } from 'date-fns'
import ProlongTrialModal from 'features/customers/components/billing/ProlongTrialModal'
import { useCustomer } from 'features/customers/hooks/useCustomer'
import { useCustomerId } from 'features/customers/hooks/useCustomerId'
import { useCustomerPaymentMethodUpdate } from 'features/customers/hooks/useCustomerPaymentMethodUpdate'
import { useCustomerSubscription } from 'features/customers/hooks/useCustomerSubscription'
import { useProlongTrial } from 'features/customers/hooks/useProlongTrial'

import { Select } from 'components/input'
import { DescriptionListTable } from 'components/table'
import { useAddNotification } from 'hooks/useAddNotification'

import { useCustomerInternalData } from '../../hooks/useCustomerInternalData'
import { paymentMethodOpts } from '../../utils/customers'

const DAYS_ON_TRIAL = 'Days on trial'
const DAYS_TO_TRIAL_END = 'Days to trial end'
const TRIALING = 'trialing'
const ROWS_TO_HIDE = [DAYS_ON_TRIAL, DAYS_TO_TRIAL_END]

const CustomerBilling = () => {
  const customerId = useCustomerId()

  const { data: customer, isLoading: isCustomerLoading } = useCustomer(customerId)
  const { data: subscription, isLoading: isSubscriptionLoading } = useCustomerSubscription()
  const { data: internalData } = useCustomerInternalData()
  const { mutateAsync: prolongTrial } = useProlongTrial()
  const { mutateAsync: updateCustomerPaymentMethod } = useCustomerPaymentMethodUpdate()

  const disabled = isCustomerLoading || isSubscriptionLoading

  const addNotification = useAddNotification()

  const [prolongDisabled, setProlongDisabled] = useState(false)
  const [open, setOpen] = useState(false)

  const handleProlongTrial = async (extraDays = 7) => {
    if (!customer || !subscription) return

    const { id: customerId } = customer
    const { trial_end } = subscription
    const trialEnd = addDays(trial_end, extraDays).getTime()

    setProlongDisabled(true)
    try {
      await prolongTrial({ customerId, trialEnd })
      addNotification(`${extraDays} days of trial successfully added`, 'success')
    } catch (_e) {
      addNotification('Could not prolong trial', 'error')
    } finally {
      setProlongDisabled(false)
      handleToggleModal()
    }
  }

  const handleToggleModal = () => setOpen(open => !open)

  const handlePaymentMethodChange = async (paymentMethod: any) => {
    if (!customer) return
    await updateCustomerPaymentMethod({ customerId: customer.id, paymentMethod })
  }

  // input-decorated Select: the HOC's typed props don't match this usage — cast (loose).
  const AnySelect = Select as any

  const config = [
    {
      title: 'Payment method',
      render: ({ payment_method }) => (
        <AnySelect
          allowEmpty={false}
          onChange={handlePaymentMethodChange}
          options={paymentMethodOpts}
          value={payment_method}
        />
      ),
    },
    {
      title: 'Is on trial?',
      render: ({ subscription }) => (subscription?.status === TRIALING ? 'Yes' : 'No'),
    },
    {
      title: 'Days on trial',
      render: ({ subscription }) => <p>{differenceInDays(Date.now(), subscription?.trial_start)}</p>,
    },
    {
      title: 'Days to trial end',
      render: ({ subscription }) => (
        <div>
          <span>{differenceInDays(subscription?.trial_end, Date.now())}</span>
          <Button
            disabled={disabled}
            onClick={handleToggleModal}
            size="small"
            style={{ marginLeft: '30px' }}
            variant="outlined">
            Add more days
          </Button>
        </div>
      ),
    },
    {
      title: 'Stripe ID',
      render: ({ stripe_customer_id }) => (
        <a href={`https://dashboard.stripe.com/customers/${stripe_customer_id}`} target="_blank" rel="noopener">
          {stripe_customer_id}
        </a>
      ),
    },
    {
      title: 'Legal Name',
      render: ({ internalData }) => internalData.legal_name || '-',
    },
    {
      title: 'Profitwell ID',
      render: ({ internalData: { profitwell_id } }) =>
        profitwell_id ? (
          <a
            href={`https://www2.profitwell.com/app/customer-profile/${profitwell_id}?tab=overview`}
            target="_blank"
            rel="noopener">
            {profitwell_id}
          </a>
        ) : (
          '-'
        ),
    },
    {
      title: 'Panda Doc Link',
      render: ({ internalData: { pandadoc_link } }) =>
        pandadoc_link
          ? pandadoc_link.split(',').map(link => (
              <>
                <a href={link.trim()} key={link} target="_blank" rel="noopener">
                  {link.trim()}
                </a>
                <br />
              </>
            ))
          : '-',
    },
    {
      title: 'Positive Data Impact',
      render: ({ internalData }) => internalData?.positive_data_impact?.toString() || '-',
    },
  ]

  const data = { ...customer, subscription, internalData }

  const fieldsToShow =
    data.subscription?.status === TRIALING ? config : config.filter(({ title }) => !ROWS_TO_HIDE.includes(title))

  return (
    <>
      <CardHeader title="Billing" />
      <DescriptionListTable config={fieldsToShow} data={data} titleCellStyle={{ width: 200 }} />
      <ProlongTrialModal
        disabled={prolongDisabled}
        onClose={handleToggleModal}
        onConfirm={handleProlongTrial}
        open={open}
      />
    </>
  )
}

export default CustomerBilling
